import PropTypes from 'prop-types';
import React, { lazy, Suspense } from 'react';

import ChevronIcon from 'Component/ChevronIcon';
import { LEFT } from 'Component/ChevronIcon/ChevronIcon.config';
import ContentWrapper from 'Component/ContentWrapper';
import Loader from 'Component/Loader';
import MyAccountCustomerServicePush from 'Component/MyAccountCustomerServicePush';
import MyAccountDocuments from 'Component/MyAccountDocuments';
import MyAccountPushPages from 'Component/MyAccountPushPages';
import MyAccountTabList from 'Component/MyAccountTabList';
import NoMatch from 'Route/NoMatch';
import {
    MyAccount as SourceMyAccount,
    MyAccountAddressBook,
    MyAccountDashboard,
    MyAccountDownloadable,
    MyAccountMyOrders,
    MyAccountMyWishlist,
    MyAccountNewsletterSubscription
} from 'SourceRoute/MyAccount/MyAccount.component';
import {
    ACCOUNT_INFORMATION,
    ADDRESS_BOOK, CUSTOMER_SERVICE, CUSTOMER_SERVICE_ISSUE,
    CustomerType,
    MY_ACCOUNT, MY_DOCUMENTS,
    MY_ORDERS,
    NEWSLETTER_SUBSCRIPTION, QUICK_ORDER
} from 'Type/Account.type';
import { isSignedIn } from 'Util/Auth';

import {
    ACCOUNT_CUSTOMER_SERVICE_ISSUE_URL,
    ACCOUNT_ORDER_URL
} from './MyAccount.config';

import './MyAccount.override.style';

export {
    MyAccountAddressBook,
    MyAccountDashboard,
    MyAccountDownloadable,
    MyAccountMyOrders,
    MyAccountMyWishlist,
    MyAccountNewsletterSubscription
};

export const ClientIssue = lazy(() => import(
    /* webpackMode: "lazy", webpackChunkName: "account-customerservice" */
    'Component/ClientIssue'
));
export const MyAccountCustomerService = lazy(() => import(
    /* webpackMode: "lazy", webpackChunkName: "account-customerservice" */
    'Component/MyAccountCustomerService'
));
export const MyAccountProQuickOrder = lazy(() => import(
    /* webpackMode: "lazy", webpackChunkName: "account-quick-order" */
    'Component/MyAccountProQuickOrder'
));

/** @namespace PeggysagePwa/Route/MyAccount/Component */
export class MyAccountComponent extends SourceMyAccount {
    static propTypes = {
        ...SourceMyAccount.propTypes,
        isMobile: PropTypes.bool.isRequired,
        customer: CustomerType.isRequired,
        onBackClick: PropTypes.func.isRequired,
        isLoading: PropTypes.bool.isRequired
    };

    renderMap = {
        ...this.renderMap,
        [CUSTOMER_SERVICE]: MyAccountCustomerService,
        [CUSTOMER_SERVICE_ISSUE]: ClientIssue,
        [QUICK_ORDER]: MyAccountProQuickOrder,
        [MY_DOCUMENTS]: MyAccountDocuments
    };

    /**
     * Override: check change of device or customer loaded
     * @param nextProps
     * @returns {boolean}
     */
    /* eslint-disable @scandipwa/scandipwa-guidelines/only-render-in-component */
    shouldComponentUpdate(nextProps) {
        const { isMobile, customer } = this.props;
        const { isMobile: nextIsMobile, customer: nextCustomer } = nextProps;

        return (
            super.shouldComponentUpdate(nextProps)
            || isMobile !== nextIsMobile
            || customer !== nextCustomer
        );
    }

    getTabContent() {
        const { activeTab, location: { pathname } } = this.props;

        if (activeTab === CUSTOMER_SERVICE && pathname.includes(ACCOUNT_CUSTOMER_SERVICE_ISSUE_URL)) {
            return this.renderMap[CUSTOMER_SERVICE_ISSUE];
        }

        return super.getTabContent();
    }
    /* eslint-enable @scandipwa/scandipwa-guidelines/only-render-in-component */

    renderTabList() {
        const {
            activeTab,
            tabMap,
            changeActiveTab,
            onSignOut,
            isMobile
        } = this.props;

        if (isMobile && activeTab !== MY_ACCOUNT) {
            return null;
        }

        return (
            <MyAccountTabList
              tabMap={ tabMap }
              activeTab={ activeTab }
              changeActiveTab={ changeActiveTab }
              onSignOut={ onSignOut }
            />
        );
    }

    renderBackLink() {
        const { isMobile, activeTab, onBackClick } = this.props;

        if (!isMobile || activeTab === MY_ACCOUNT) {
            return null;
        }

        return (
            <div block="MyAccount" elem="Back">
                <button
                  type="button"
                  block="MyAccount"
                  elem="BackLink"
                  onClick={ onBackClick }
                >
                    <ChevronIcon direction={ LEFT } />
                    { __('Back') }
                </button>
            </div>
        );
    }

    renderHello() {
        const {
            isMobile,
            activeTab,
            customer: {
                firstname,
                is_pro,
                is_student
            }
        } = this.props;

        // Only in dashboard on mobile
        if (isMobile && activeTab !== MY_ACCOUNT) {
            return null;
        }

        const title = firstname ? __('Bonjour %s', firstname) : __('Bonjour');

        return (
            <h2 block="MyAccount" elem="Hello" mods={ { is_pro, is_student } }>
                <span block="MyAccount" elem="HelloIcon" />
                { title }
            </h2>
        );
    }

    renderHeading() {
        const {
            isMobile,
            activeTab,
            tabMap,
            tabName
        } = this.props;

        if (isMobile && activeTab === MY_ACCOUNT) {
            return null;
        }

        const { title } = tabMap[activeTab];

        return (
            <h2 block="MyAccount" elem="Heading">
                { title || tabName }
                { this.renderSubHeading() }
            </h2>
        );
    }

    renderTabContent() {
        const {
            activeTab,
            tabMap,
            isEditingActive,
            match,
            changeTabName,
            setTabSubheading
        } = this.props;

        const TabContent = this.getTabContent();

        return (
            <div
              block="MyAccount"
              elem="TabContent"
              mods={ { activeTab } }
            >
                { this.renderHeading() }
                <Suspense fallback={ <Loader /> }>
                    <TabContent
                      isEditingActive={ isEditingActive }
                      match={ match }
                      changeTabName={ changeTabName }
                      tabMap={ tabMap }
                      setTabSubheading={ setTabSubheading }
                    />
                </Suspense>
                { [ACCOUNT_INFORMATION, ADDRESS_BOOK, NEWSLETTER_SUBSCRIPTION].includes(activeTab) && (
                    <MyAccountCustomerServicePush />
                ) }
            </div>
        );
    }

    renderPushPages() {
        const {
            activeTab,
            location: { pathname },
            customer: { is_pro, is_student }
        } = this.props;

        // Show only on dashboard and order history page (but not order details)
        if (!(activeTab === MY_ACCOUNT || (activeTab === MY_ORDERS && !pathname.includes(ACCOUNT_ORDER_URL)))) {
            return null;
        }

        return (
            <MyAccountPushPages
              mix={ { block: 'MyAccount', elem: 'PushPages', mods: { is_pro, is_student } } }
            />
        );
    }

    renderContentMobile() {
        return (
            <>
                { this.renderBackLink() }
                { this.renderHello() }
                { this.renderTabContent() }
                { this.renderTabList() }
            </>
        );
    }

    renderContentDesktop() {
        const { isLoading } = this.props;

        return (
            <>
                <div block="MyAccount" elem="Sidebar">
                    { this.renderHello() }
                    { this.renderTabList() }
                </div>
                <div block="MyAccount" elem="Content">
                    <Loader isLoading={ isLoading } />
                    { this.renderTabContent() }
                </div>
            </>
        );
    }

    /**
     * Override
     * @returns {JSX.Element|*}
     */
    renderContent() {
        const {
            activeTab,
            isTabEnabled,
            isMobile
        } = this.props;

        if (!isSignedIn()) {
            return this.renderLoginOverlay();
        }

        if (!isTabEnabled(activeTab)) {
            return <NoMatch />;
        }

        return (
            <>
                <ContentWrapper
                  label={ __('My Account page') }
                  wrapperMix={ { block: 'MyAccount', elem: 'Wrapper' } }
                >
                    { isMobile && this.renderContentMobile() }
                    { !isMobile && this.renderContentDesktop() }
                </ContentWrapper>
                <ContentWrapper
                  label={ __('My Account highlighted pages') }
                  wrapperMix={ { block: 'MyAccount', elem: 'WrapperPushPages' } }
                  isNotSection
                >
                    { this.renderPushPages() }
                </ContentWrapper>
            </>
        );
    }
}

export default MyAccountComponent;
